import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Badge, 
  Button,
  Spinner, 
  Tab, 
  Nav, 
  ListGroup, 
  Modal,
  Form,
  Alert,
  ProgressBar
} from 'react-bootstrap';
import { 
  FaStar, 
  FaUser, 
  FaClock, 
  FaCalendarAlt, 
  FaUsers, 
  FaPlayCircle, 
  FaFilePdf, 
  FaLock, 
  FaCheck, 
  FaDownload,
  FaShoppingCart,
  FaEye,
  FaInfo
} from 'react-icons/fa';
import { 
  getCourseDetails, 
  validateAndApplyCourseCode,
  checkEnrollment,
  updateVideoProgress
} from '../../services/courseService';
import { Course, Lesson, VideoProgress } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import VideoPlayer from '../../components/VideoPlayer';

const CourseDetailPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState<Course & { lessons?: Lesson[] }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [subscribeError, setSubscribeError] = useState<string | null>(null);
  const [subscribeSuccess, setSubscribeSuccess] = useState<string | null>(null);
  const [courseCode, setCourseCode] = useState('');
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [showLessonModal, setShowLessonModal] = useState(false);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [videoProgress, setVideoProgress] = useState<{ [key: string]: number }>({});
  const [overallProgress, setOverallProgress] = useState(0);
  const [enrollmentData, setEnrollmentData] = useState<any>(null);

  useEffect(() => {
    if (courseId) {
      fetchCourseDetails();
    }
  }, [courseId]);

  useEffect(() => {
    const checkUserEnrollment = async () => {
      if (currentUser && courseId) {
        try {
          const enrollmentStatus = await checkEnrollment(courseId, currentUser.uid);
          setIsEnrolled(enrollmentStatus.isEnrolled);
          setVideoProgress(enrollmentStatus.videoProgress || {});
          
          if (enrollmentStatus.isEnrolled && enrollmentStatus.enrollmentData) {
            setEnrollmentData(enrollmentStatus.enrollmentData);
            setOverallProgress(enrollmentStatus.enrollmentData.progress || 0);
          }
        } catch (error) {
          console.error('Error checking enrollment status:', error);
        }
      }
    };
    
    checkUserEnrollment();
  }, [currentUser, courseId]);

  const fetchCourseDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!courseId) {
        throw new Error('معرف الكورس غير صحيح');
      }
      
      const courseDetails = await getCourseDetails(courseId);
      setCourse(courseDetails);
    } catch (error) {
      console.error('Error fetching course details:', error);
      setError('حدث خطأ أثناء تحميل تفاصيل الكورس');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (!currentUser) {
      setSubscribeError('يجب تسجيل الدخول أولاً للاشتراك في الكورس');
      return;
    }

    try {
      setSubscribeLoading(true);
      setSubscribeError(null);
      setSubscribeSuccess(null);
      
      if (!courseId || !courseCode.trim()) {
        throw new Error('يرجى إدخال كود الكورس');
      }
      
      await validateAndApplyCourseCode(courseCode.trim(), currentUser.uid);
      setSubscribeSuccess('تم الاشتراك في الكورس بنجاح!');
      setIsEnrolled(true);
      setCourseCode('');
      
      // Refresh course details to update enrollment status
      await fetchCourseDetails();
    } catch (error: any) {
      console.error('Error subscribing to course:', error);
      setSubscribeError(error.message || 'حدث خطأ أثناء الاشتراك في الكورس');
    } finally {
      setSubscribeLoading(false);
    }
  };

  const handleLessonClick = (lesson: Lesson) => {
    if (!currentUser) {
      navigate('/login', { state: { from: `/courses/${courseId}` } });
      return;
    }

    // تحقق من حالة اشتراك الطالب
    if (!isEnrolled) {
      // إذا لم يكن الطالب مشتركًا، اعرض نافذة طلب كود التفعيل
      setShowSubscribeModal(true);
      return;
    }

    // إذا كان الطالب مشتركًا، اعرض الدرس
    setSelectedLesson(lesson);
    setShowLessonModal(true);
  };

  const handleVideoProgress = async (lessonId: string, progress: number) => {
    if (!currentUser || !courseId) return;

    try {
      await updateVideoProgress(courseId, lessonId, currentUser.uid, progress);
      
      // Update local state
      setVideoProgress(prev => ({
        ...prev,
        [lessonId]: progress
      }));
      
      // Refresh course details if progress is complete (90% or more)
      if (progress >= 90) {
        await fetchCourseDetails();
      }
    } catch (error) {
      console.error('Error updating video progress:', error);
    }
  };

  const renderStars = (rating?: number) => {
    const stars = [];
    const ratingValue = rating || 0;
    for (let i = 0; i < 5; i++) {
      stars.push(
        <FaStar
          key={i}
          className={i < ratingValue ? 'text-warning' : 'text-muted'}
        />
      );
    }
    return stars;
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" />
        <p className="mt-2">جاري تحميل تفاصيل الكورس...</p>
      </div>
    );
  }

  if (error || !course) {
    return (
      <Container className="my-5">
        <Alert variant="danger">
          {error || 'لم يتم العثور على الكورس'}
        </Alert>
        <div className="text-center mt-4">
          <Link to="/courses" className="text-white text-decoration-none">
            العودة إلى الكورسات
          </Link>
        </div>
      </Container>
    );
  }

  const renderLessonModal = () => (
    <Modal
      show={showLessonModal}
      onHide={() => setShowLessonModal(false)}
      size="lg"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>{selectedLesson?.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedLesson?.videoUrl ? (
          <div className="mb-4">
            <VideoPlayer
              videoUrl={selectedLesson.videoUrl}
              courseId={courseId || ''}
              lessonId={selectedLesson.id}
              userId={currentUser?.uid || ''}
              initialProgress={videoProgress[selectedLesson.id] || 0}
              onComplete={() => fetchCourseDetails()}
            />
          </div>
        ) : (
          <Alert variant="info">
            هذا الدرس لا يحتوي على فيديو
          </Alert>
        )}
        <div className="lesson-description mb-4">
          <h5>وصف الدرس</h5>
          <p>{selectedLesson?.description}</p>
        </div>
        {selectedLesson?.attachments && selectedLesson.attachments.length > 0 && (
          <div className="lesson-attachments">
            <h5>الملفات المرفقة</h5>
            <ListGroup>
              {selectedLesson.attachments.map((attachment, index) => (
                <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                  <span>
                    <FaFilePdf className="me-2" />
                    {attachment.name}
                  </span>
                  <a
                    className="btn btn-outline-primary btn-sm"
                    href={attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                  >
                    <FaDownload className="me-1" />
                    تحميل
                  </a>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowLessonModal(false)}>
          إغلاق
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const renderSubscribeModal = () => (
    <Modal
      show={showSubscribeModal}
      onHide={() => {
        setShowSubscribeModal(false);
        setSubscribeError(null);
        setSubscribeSuccess(null);
        setCourseCode('');
      }}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>الاشتراك في الكورس</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {course && (
          <div className="text-center mb-4">
            <h5>{course.title}</h5>
            {course.imageUrl && (
              <img 
                src={course.imageUrl} 
                alt={course.title} 
                className="img-fluid rounded mb-3" 
                style={{ maxHeight: '150px' }}
              />
            )}
          </div>
        )}
        
        <Alert variant="info">
          <FaInfo className="me-2" /> للوصول إلى محتوى هذا الكورس، يجب أن تكون مشتركًا. يرجى إدخال كود التفعيل الخاص بالكورس.
        </Alert>
        
        {subscribeError && (
          <Alert variant="danger">{subscribeError}</Alert>
        )}
        
        {subscribeSuccess && (
          <Alert variant="success">{subscribeSuccess}</Alert>
        )}
        
        <Form onSubmit={(e) => {
          e.preventDefault();
          handleSubscribe();
        }}>
          <Form.Group className="mb-3">
            <Form.Label>كود الاشتراك</Form.Label>
            <Form.Control
              type="text"
              placeholder="أدخل كود الاشتراك"
              value={courseCode}
              onChange={(e) => setCourseCode(e.target.value)}
              disabled={subscribeLoading}
            />
          </Form.Group>
          
          <div className="d-grid">
            <Button 
              variant="primary" 
              type="submit"
              disabled={subscribeLoading}
            >
              {subscribeLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  جاري التحقق...
                </>
              ) : 'اشترك الآن'}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );

  return (
    <div className="course-detail-page py-4">
      <Container>
        {/* Course Header */}
        <div className="course-header mb-4">
          <Row>
            <Col lg={8}>
              <div className="d-flex align-items-center mb-2">
                <Link to="/courses" className="text-decoration-none text-muted">
                  الكورسات
                </Link>
                <span className="mx-2">/</span>
                {course.subject && (
                  <>
                    <Link to={`/courses?subject=${course.subject}`} className="text-decoration-none text-muted">
                      {course.subject}
                    </Link>
                    <span className="mx-2">/</span>
                  </>
                )}
                <span className="text-primary">{course.title}</span>
              </div>
              <h1 className="course-title mb-3">{course.title}</h1>
              
              <div className="course-meta d-flex flex-wrap align-items-center mb-3">
                <div className="me-4 mb-2">
                  <FaUser className="me-1 text-muted" />
                  <span>{course.teacherName || 'معلم المنصة'}</span>
                </div>
                <div className="me-4 mb-2">
                  <span className="d-inline-flex align-items-center">
                    {renderStars(course.rating)}
                    <span className="ms-1">({course.rating || 0})</span>
                  </span>
                </div>
                <div className="me-4 mb-2">
                  <FaUsers className="me-1 text-muted" />
                  <span>{course.studentsCount || 0} طالب</span>
                </div>
                <div className="me-4 mb-2">
                  <FaCalendarAlt className="me-1 text-muted" />
                  <span>
                    {course.createdAt 
                      ? new Date(course.createdAt).toLocaleDateString('ar-EG') 
                      : 'غير محدد'}
                  </span>
                </div>
                {course.duration && (
                  <div className="mb-2">
                    <FaClock className="me-1 text-muted" />
                    <span>{course.duration} ساعة</span>
                  </div>
                )}
              </div>
              
              {course.subject && (
                <Badge bg="primary" className="mb-3">
                  {course.subject}
                </Badge>
              )}
            </Col>
            <Col lg={4}>
              <Card className="course-action-card shadow-sm">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3 className="course-price mb-0">
                      {course.price ? `${course.price} ج.م` : 'مجاني'}
                    </h3>
                    {isEnrolled ? (
                      <Badge bg="success" className="p-2">
                        <FaCheck className="me-1" /> مشترك
                      </Badge>
                    ) : null}
                  </div>
                  
                  <div className="course-stats mb-3">
                    <div className="d-flex justify-content-between mb-2">
                      <span>عدد الدروس:</span>
                      <strong>{course.lessons?.length || 0}</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span>المدة الإجمالية:</span>
                      <strong>{course.duration || 0} ساعة</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span>المستوى:</span>
                      <strong>{course.level || 'مبتدئ'}</strong>
                    </div>
                    
                    {isEnrolled && (
                      <div className="mt-3">
                        <div className="d-flex justify-content-between mb-1">
                          <span>تقدمك في الكورس:</span>
                          <strong>{overallProgress}%</strong>
                        </div>
                        <ProgressBar 
                          now={overallProgress} 
                          variant={overallProgress >= 90 ? 'success' : 'primary'} 
                          className="mb-2"
                        />
                      </div>
                    )}
                  </div>
                  
                  {isEnrolled ? (
                    <Button 
                      variant="success" 
                      className="w-100 mb-2"
                      onClick={() => setActiveTab('lessons')}
                    >
                      <FaPlayCircle className="me-1" /> متابعة التعلم
                    </Button>
                  ) : (
                    <Button 
                      variant="primary" 
                      className="w-100 mb-2"
                      onClick={() => setShowSubscribeModal(true)}
                    >
                      <FaShoppingCart className="me-1" /> اشترك الآن
                    </Button>
                  )}
                  
                  <div className="text-center mt-3">
                    <small className="text-muted">
                      الاشتراك يمنحك وصولاً كاملاً لجميع محتويات الكورس
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
        
        {/* Course Image */}
        <div className="course-image-container mb-4">
          {course.imageUrl ? (
            <img 
              src={course.imageUrl} 
              alt={course.title} 
              className="course-image img-fluid rounded shadow"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.onerror = null;
                target.src = 'https://via.placeholder.com/1200x600?text=كورس';
              }}
            />
          ) : (
            <img 
              src="https://via.placeholder.com/1200x600?text=كورس" 
              alt={course.title} 
              className="course-image img-fluid rounded shadow"
            />
          )}
        </div>
        
        {/* Course Content Tabs */}
        <Tab.Container id="course-tabs" activeKey={activeTab} onSelect={(key) => setActiveTab(key || 'overview')}>
          <Row>
            <Col lg={12}>
              <Nav variant="tabs" className="course-tabs mb-4">
                <Nav.Item>
                  <Nav.Link eventKey="overview">نظرة عامة</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="lessons">الدروس {course.lessons?.length ? `(${course.lessons.length})` : ''}</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="requirements">المتطلبات</Nav.Link>
                </Nav.Item>
              </Nav>
            </Col>
          </Row>
          
          <Row>
            <Col lg={12}>
              <Tab.Content>
                {/* Overview Tab */}
                <Tab.Pane eventKey="overview">
                  <div className="course-description mb-4">
                    <h3 className="section-title mb-3">وصف الكورس</h3>
                    <div className="description-content">
                      {course.description ? (
                        <p>{course.description}</p>
                      ) : (
                        <p className="text-muted">لا يوجد وصف متاح لهذا الكورس.</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="course-objectives mb-4">
                    <h3 className="section-title mb-3">ماذا ستتعلم</h3>
                    {course.objectives ? (
                      <div className="objectives-content">
                        {course.objectives.split('\n').map((objective, index) => (
                          <div key={index} className="objective-item d-flex align-items-start mb-2">
                            <FaCheck className="text-success mt-1 me-2" />
                            <div>{objective}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted">لم يتم تحديد أهداف لهذا الكورس.</p>
                    )}
                  </div>
                </Tab.Pane>
                
                {/* Lessons Tab */}
                <Tab.Pane eventKey="lessons">
                  <div className="course-lessons">
                    <h3 className="section-title mb-3">محتوى الكورس</h3>
                    
                    {course.lessons && course.lessons.length > 0 ? (
                      <div className="lessons-list">
                        <ListGroup>
                          {course.lessons.map((lesson, index) => (
                            <ListGroup.Item 
                              key={lesson.id}
                              className="lesson-item d-flex justify-content-between align-items-center"
                              action
                              onClick={() => handleLessonClick(lesson)}
                            >
                              <div className="d-flex align-items-center">
                                <div className="lesson-number me-3">
                                  <Badge bg="light" text="dark" className="rounded-circle p-2">
                                    {index + 1}
                                  </Badge>
                                </div>
                                <div className="lesson-info">
                                  <div className="lesson-title fw-bold">
                                    {lesson.title}
                                  </div>
                                  <div className="lesson-meta text-muted">
                                    <small>
                                      {lesson.duration ? `${lesson.duration} دقيقة` : 'مدة غير محددة'}
                                      {lesson.isFree && (
                                        <Badge bg="success" className="ms-2">مجاني</Badge>
                                      )}
                                    </small>
                                  </div>
                                </div>
                              </div>
                              <div className="lesson-actions d-flex align-items-center">
                                {videoProgress[lesson.id] ? (
                                  <div className="me-3">
                                    <ProgressBar 
                                      now={videoProgress[lesson.id]} 
                                      variant={videoProgress[lesson.id] >= 90 ? 'success' : 'primary'} 
                                      style={{ width: '60px', height: '8px' }} 
                                    />
                                    <small className="text-muted">{videoProgress[lesson.id]}%</small>
                                  </div>
                                ) : null}
                                
                                {isEnrolled ? (
                                  <Button variant="outline-primary" size="sm">
                                    <FaPlayCircle className="me-1" /> شاهد
                                  </Button>
                                ) : (
                                  <Button variant="outline-warning" size="sm" onClick={() => setShowSubscribeModal(true)}>
                                    <FaLock className="me-1" /> اشترك للمشاهدة
                                  </Button>
                                )}
                              </div>
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                      </div>
                    ) : (
                      <Alert variant="info">
                        لا توجد دروس متاحة لهذا الكورس حالياً.
                      </Alert>
                    )}
                  </div>
                </Tab.Pane>
                
                {/* Requirements Tab */}
                <Tab.Pane eventKey="requirements">
                  <div className="course-requirements">
                    <h3 className="section-title mb-3">متطلبات الكورس</h3>
                    
                    {course.requirements ? (
                      <div className="requirements-content">
                        {course.requirements.split('\n').map((requirement, index) => (
                          <div key={index} className="requirement-item d-flex align-items-start mb-2">
                            <FaCheck className="text-primary mt-1 me-2" />
                            <div>{requirement}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted">لا توجد متطلبات محددة لهذا الكورس.</p>
                    )}
                  </div>
                </Tab.Pane>
              </Tab.Content>
            </Col>
          </Row>
        </Tab.Container>
      </Container>
      
      {renderLessonModal()}
      {renderSubscribeModal()}
    </div>
  );
};

export default CourseDetailPage;
